import React, { useCallback, useMemo, useState } from 'react';
import { useKeywordStatsRecompute } from '../../../shared/api/hooks/monitor/keyword/useKeywordStatsRecompute';
import { useQueryClient } from '@tanstack/react-query';
import './ProjectsPreviewPage.scss';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import ProjectPreviewSkeleton from './components/ProjectPreviewSkeleton';
import { PROJECTS } from '../../../shared/routes/path';
import { Trans, useTranslation } from 'react-i18next';
import Title from '../../../shared/components/title/Title';
import PromptsTable from '../../../shared/components/prompts-table/PromptsTable';
import { useFilters } from '../../../shared/contexts/filters/FiltersContext';
import useFiltersInitializing from '../../../shared/hooks/filters/UseFiltersInitializing';
import useMonitorPeriodAndChatType from '../../../shared/hooks/projects/preview/UseMonitorPeriodAndChatType';
import useProjectNavigate from '../../../shared/hooks/projects/preview/UseProjectNavigate';
import FilterPanel from '../../../shared/components/filter-panel/FilterPanel';
import MoreBtn from '../../../shared/components/common/more-btn/MoreBtn';
import { Button } from 'primereact/button';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import useExport from '../../../shared/hooks/global/UseExport';
import useChangeName from '../../../shared/hooks/projects/UseChangeName';
import useRemoveProject from '../../../shared/hooks/projects/UseRemoveProject';
import useExportData from '../../../shared/hooks/projects/UseExportData';
import usePermission from '../../../shared/hooks/global/UsePermission';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../../shared/components/common/mobile-menu/MobilePageHeader';
import useCanManage from '../../../shared/hooks/account/UseCanManage';
import useChooseKeywords from '../../../shared/hooks/projects/UseChooseKeywords';
import { ProjectStatsTiles, ShareOfVoiceChart } from './components';
import KeySources from './components/key-sources/KeySources';
import { useGetTags } from '../../../shared/api/hooks/tags/useGetTags';
import { useGetMonitorsForProject } from '../../../shared/api/hooks/monitor/useGetMonitorsForProject';
import { useGetCommonKeywordsWithTags } from '../../../shared/api/hooks/monitor/keyword/useGetCommonKeywordsWithTags';
import { useGetKeywordsDailyStatsForProject } from '../../../shared/api/hooks/monitor/keyword/stats/useGetKeywordsDailyStatsForProject';
import { ProjectPreviewEmptyState } from './components/empty-state/ProjectPreviewEmptyState';
import { useGetProjectDailyStats } from '../../../shared/api/hooks/project/stats/useGetProjectDailyStats';
import { useGetEvents } from '../../../shared/api/hooks/monitor/events/useGetEvents';
import BrandPositionChart from './components/brand-position-chart/BrandPositionChart';
import { BrandsTable } from './components/brand-table/BrandsTable';
import ShareOfVoiceBarChart from './components/sov-bar-chart/ShareOfVoiceBarChart';
import { useGetProjectAllBrandsStats } from '../../../shared/api/hooks/project/stats/useGetProjectAllBrandsStats';
import { useGetProjectSovPerModel } from '../../../shared/api/hooks/project/stats/useGetProjectSovPerModel';
import { useGetProjectVisibilityChart } from '../../../shared/api/hooks/project/stats/useGetProjectVisibilityChart';
import { useGetProjectSovTimeSeries } from '../../../shared/api/hooks/project/stats/useGetProjectSovTimeSeries';
import { useGetProjectModelDataDateRanges } from '../../../shared/api/hooks/project/stats/useGetProjectModelDataDateRanges';
import { useGetLiveBrandKeys } from '../../../shared/api/hooks/project/stats/useGetLiveBrandKeys';
import { GET_PROJECTS_LIST_KPIS } from '../../../shared/api/query/project/stats/keys';
import { useGetProjectVisibilityPerMonitor } from '../../../shared/api/hooks/project/stats/useGetProjectVisibilityPerMonitor';
import { useGetSourcesForProject } from '../../../shared/api/hooks/monitor/data/sources/useGetSourcesForProject';
import {
  createVisibilityMatrix,
  createSovOverTimeMatrix,
} from './helpers/ProjectPreviewHelper';
import { PeriodType } from '../../../shared/types/prompt/PeriodType';
import { getEnabledModelKeys } from '../../../shared/helpers/projectSetupProgress';
import {
  expandSelectedChatTypes,
  expandSelectedChatTypesToMonitorTypes,
} from '../../../shared/helpers/MonitorTypeHelpers';
import { useChartColors } from './hooks/UseChartColors';
import { useKeywordMapper } from './hooks/UseKeywordMapper';
import { usePromptsData } from './hooks/UsePromptsData';
import { useProjectStatsMatrix } from './hooks/UseProjectStatsMatrix';
import { useShareOfVoiceData } from './hooks/UseShareOfVoiceData';
import { useAllModelsStatsMatrix } from './hooks/UseAllModelsStatsMatrix';
import InsightsBox from './components/insights/InsightsBox';
import ShareOfVoiceOverTimeChart from './components/sov-over-time-chart/ShareOfVoiceOverTimeChart';
import { useCurrentBrandGeneralStats } from './hooks/UseCurrentBrandGeneralStats';
import { useVisibilityTexts } from './hooks/UseVisibilityTexts';
import { findTagIdInKeyword, getBrandDisplayName, resolveBrandKey } from '../../../shared/helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../shared/helpers/tags/TagsHelper';
import { TAG_NAME } from '../../../shared/utils/urls';
import { useGetReport } from '../../../shared/api/hooks/report/useGetReport';
import html2pdf from 'html2pdf.js';
import { PdfReportResponse } from '../../../shared/models/report/PdfReportResponse';
import { useFirstViewTracking } from './hooks/UseFirstViewTracking';
import { useGetProject } from '../../../shared/api/hooks/project/useGetProject';
import ChangeKeywordBox from '../../../shared/components/change-keyword-box/ChangeKeywordBox';
import { useModelsWithoutData } from './hooks/UseModelsWithoutData';
import { useEffectiveAddons } from '../../../shared/contexts/effective-features/EffectiveFeaturesContext';
import { useGetAnomalyIncidents } from '../../../shared/api/hooks/anomaly-detection/useGetAnomalyIncidents';
import { usePromptGroups } from '../../../shared/hooks/prompt/UsePromptGroups';
import { PromptGroupItem } from '../../../shared/components/prompts-table/PromptsTable';
import { Dropdown } from 'primereact/dropdown';
import PromptGroupDropdown from '../../../shared/components/prompt-group-dropdown/PromptGroupDropdown';
import usePromptGroupId, { ALL_PROMPT_GROUPS_ID } from '../../../shared/hooks/filters/UsePromptGroupId';
import { Dialog } from 'primereact/dialog';
import { regenerateSuggestedQuestions } from '../../../shared/services/projects/ProjectService';
import useToast from '../../../shared/hooks/global/UseToast';
import TopDomainsTable from './components/top-domains-table/TopDomainsTable';
import AiCitations from './components/ai-citations/AiCitations';
import { blendBrandScore } from '../../../shared/helpers/brand-score/BrandScoreHelper';

const ProjectsPreviewPageV2: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  let { projectId, type, period, chat } = useParams();
  const projectIdNumber = Number(projectId);

  const { t } = useTranslation();
  const { monitorPeriod, range, selectedChatType, selectedChatTypes } = useFilters();
  const filtersInitializing = useFiltersInitializing();
  const {
    projectResponse,
    accountResponse,
    refetchData,
    refetchAccountSilently,
    refetchProjectsSilently,
  } = useAccount();
  const { canManage } = useCanManage();
  const { canExport } = useExport();

  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingBrandScore, setIsDownloadingBrandScore] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regenerateLanguage, setRegenerateLanguage] = useState<{ name: string; type: string } | null>(
    null
  );
  const [isRegenerating, setIsRegenerating] = useState(false);
  const { showToast, toastElement } = useToast();

  const regenerateLanguageOptions = useMemo(
    () => [
      { name: 'PL', type: 'PL' },
      { name: 'EN', type: 'EN' },
    ],
    []
  );
  const [searchParams] = useSearchParams();
  const hasExplicitModelFilter = searchParams.get('models') !== null;
  const filterChatTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0)
      return undefined;
    return Array.from(expandSelectedChatTypes(selectedChatTypes));
  }, [hasExplicitModelFilter, selectedChatTypes]);
  // Sources / Top Domains / Key Sources filter on mds.type (MonitorTypes), not chat types — map the
  // selected families to their monitor types or the filter matches nothing. Brand stats above keep
  // using filterChatTypes (they filter on a ChatType column).
  const filterMonitorTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0)
      return undefined;
    return expandSelectedChatTypesToMonitorTypes(selectedChatTypes);
  }, [hasExplicitModelFilter, selectedChatTypes]);
  const { promptGroupId } = usePromptGroupId();
  const hasPromptGroupsAddon = !!accountResponse?.addons?.includes('PROMPT_GROUP');
  const hasAnomalyDetectionAddon = !!accountResponse?.addons?.includes('ANOMALY_DETECTION');
  // Owner's addons when a superadmin views a client's project; own otherwise.
  const effectiveAddons = useEffectiveAddons();
  const hasNewStats = effectiveAddons.includes('NEW_STATS');
  const hasBrandGrouping = effectiveAddons.includes('KEYWORDS_PAGE');
  // "Per model" breakdowns (SoV by AI Chatbots, Top Domains) carry no information when the filter
  // narrows the view to a single model — one column remains. `selectedChatType` won't catch this:
  // with a `?models=` filter it stays `ALL`.
  const isSingleModelSelected = hasExplicitModelFilter && selectedChatTypes.length === 1;
  const canUseAnomaliesForSelection = !hasExplicitModelFilter || selectedChatTypes.length === 1;
  const canShowAnomalyMarkers =
    hasAnomalyDetectionAddon && monitorPeriod === 'WEEKLY' && canUseAnomaliesForSelection;
  const { data: promptGroups } = usePromptGroups(projectIdNumber);

  const startDate = range?.[0] || null;
  const endDate = range?.[1] || null;

  // Real first/last data date per model (within the calendar range) — only for NEW_STATS, where the
  // prompts table surfaces archival models. Feeds the per-model "?" tooltip.
  const { modelDataDateRanges } = useGetProjectModelDataDateRanges(
    projectIdNumber,
    startDate,
    endDate,
    hasNewStats
  );
  const modelDataRanges = useMemo(
    () =>
      Object.fromEntries(
        modelDataDateRanges.map((r) => [r.model, { from: r.firstDate, to: r.lastDate }])
      ),
    [modelDataDateRanges]
  );

  const {
    project,
    refetch: reloadProject,
    isLoading: isProjectLoading,
  } = useGetProject(projectIdNumber);

  const projectsAccess = usePermission('PROJECTS');
  const isMobile = useIsMobile();

  const { refetch: reloadReport } = useGetReport(projectIdNumber, startDate, endDate, period);

  const { tags, refetch: reloadTags, isLoading: isTagsLoading } = useGetTags(projectIdNumber);
  const {
    monitorPrompts,
    isLoading: isMonitorPromptsLoading,
    refetch: reloadMonitorPrompts,
  } = useGetMonitorsForProject(projectIdNumber);

  // "Collecting" = a prompt is actively producing its first data now (PENDING/REFRESHING). Deferred or
  // terminal statuses (QUEUED_FOR_NEXT_DAY, ERROR, DELETED, TO_DELETE) must NOT count — otherwise the
  // page gets stuck on the "Computing" loader for data that won't arrive today.
  const hasAnyPendingQuestions = !!monitorPrompts?.some((promptData) =>
    ['PENDING', 'REFRESHING'].includes(promptData.status)
  );

  // Once ANY prompt has reached ACTIVE (querying ran at least once), the project is past its first
  // collection — a still-pending/queued prompt must NOT reblock the whole stats view with the
  // "computing" loader. Only a truly fresh project (pending, nothing active yet) shows collecting;
  // otherwise we show the real state (data, or the standard empty state).
  const hasAnyActivePrompt = !!monitorPrompts?.some((promptData) => promptData.status === 'ACTIVE');
  const isInitialCollecting = hasAnyPendingQuestions && !hasAnyActivePrompt;

  const filteredMonitorPrompts = useMemo(() => {
    if (!monitorPrompts) return null;
    if (promptGroupId === ALL_PROMPT_GROUPS_ID) return monitorPrompts;
    return monitorPrompts.filter((p) => p.promptGroupId === promptGroupId);
  }, [monitorPrompts, promptGroupId]);

  const {
    commonKeywords,
    isLoading: isCommonKeywordsLoading,
    refetch: reloadCommonKeywords,
  } = useGetCommonKeywordsWithTags(projectIdNumber, hasAnyPendingQuestions);

  const {
    keywordsDailyStatsForProject,
    isLoading: isKeywordDailyStatsForProjectLoading,
    isRefreshing: isKeywordDailyStatsForProjectRefreshing,
    refetch: reloadKeywordDailyStatsForProject,
  } = useGetKeywordsDailyStatsForProject(
    projectIdNumber,
    selectedChatType,
    monitorPeriod,
    startDate,
    endDate,
    filteredMonitorPrompts,
    filterMonitorTypes
  );

  const {
    projectDailyStats,
    isLoading: isProjectDailyStatsLoading,
    isRefreshing: isProjectDailyStatsRefreshing,
    refetch: reloadProjectDailyStats,
  } = useGetProjectDailyStats(
    projectIdNumber,
    filteredMonitorPrompts,
    monitorPeriod,
    selectedChatType,
    startDate,
    endDate,
    filterChatTypes
  );

  const {
    events,
    isLoading: isEventsLoading,
    refetch: reloadEvents,
  } = useGetEvents(projectIdNumber, monitorPeriod, selectedChatType, startDate, endDate);

  const {
    brandScoreAnomalies,
    averagePositionAnomalies,
    visibilityAnomalies,
    metricSemantics,
    isLoading: isAnomalyIncidentsLoading,
    refetch: reloadAnomalyIncidents,
  } = useGetAnomalyIncidents(
    projectIdNumber,
    startDate,
    endDate,
    new Set(filteredMonitorPrompts ? filteredMonitorPrompts.map((prompt) => prompt.keyword) : []),
    filterChatTypes,
    canShowAnomalyMarkers
  );

  const {
    allBrandsStatsResponse,
    visibility: projectVisibility,
    isLoading: isAllBrandsStatsResponseLoading,
    isRefreshing: isAllBrandsStatsResponseRefreshing,
    refetch: reloadAllBrandsStatsResponse,
  } = useGetProjectAllBrandsStats(
    projectIdNumber,
    filteredMonitorPrompts,
    selectedChatType,
    monitorPeriod,
    startDate,
    endDate,
    filterChatTypes,
    true // V2: SoV = 0-filled average of per-prompt SoV (backend ?sovMode=perPrompt)
  );

  // Per-(model, brand) per-prompt SoV → feeds the "SoV by AI Chatbots" bar chart so it matches the
  // pie / Brands ranking (same backend per-prompt method, broken down by chat_type). V2-only.
  const {
    sovPerModel,
    isLoading: isSovPerModelLoading,
    isRefreshing: isSovPerModelRefreshing,
    refetch: reloadSovPerModel,
  } = useGetProjectSovPerModel(
    projectIdNumber,
    filteredMonitorPrompts,
    selectedChatType,
    startDate,
    endDate,
    filterChatTypes
  );

  const {
    visibilityChart,
    isLoading: isVisibilityChartLoading,
    isRefreshing: isVisibilityChartRefreshing,
    refetch: reloadVisibilityChart,
  } = useGetProjectVisibilityChart(
    projectIdNumber,
    filteredMonitorPrompts,
    selectedChatType,
    monitorPeriod,
    startDate,
    endDate,
    filterChatTypes
  );

  const {
    sovTimeSeries,
    isLoading: isSovTimeSeriesLoading,
    isRefreshing: isSovTimeSeriesRefreshing,
    refetch: reloadSovTimeSeries,
  } = useGetProjectSovTimeSeries(
    projectIdNumber,
    filteredMonitorPrompts,
    selectedChatType,
    monitorPeriod,
    startDate,
    endDate,
    filterChatTypes
  );

  // Live brand keys (data under current models) — used to hide orphan brands from the brand-selection
  // surfaces (ChangeKeywordBox + the "Select brands for analysis" dialog).
  const { liveBrandKeys } = useGetLiveBrandKeys(
    projectIdNumber,
    filteredMonitorPrompts,
    selectedChatType,
    startDate,
    endDate,
    filterChatTypes
  );

  const {
    visibilityPerMonitor,
    isRefreshing: isVisibilityPerMonitorRefreshing,
    refetch: reloadVisibilityPerMonitor,
  } =
    useGetProjectVisibilityPerMonitor(
      projectIdNumber,
      filteredMonitorPrompts,
      selectedChatType,
      startDate,
      endDate,
      filterChatTypes
    );

  // Feeds the KeySources "Cytowania AI" aggregate + domain filter (all-sources data, computed
  // exactly like V1 so the numbers match). The paginated table inside KeySources uses its own
  // server-paginated endpoint. getSources is fast now (verification capped + parallel).
  const { sourcesForProject, refetch: reloadSourcesForProject } = useGetSourcesForProject(
    projectIdNumber,
    filteredMonitorPrompts,
    selectedChatType,
    startDate,
    endDate
  );

  const queryClient = useQueryClient();

  const onRefresh = useCallback(async () => {
    // ProjectsPage renders project rows from server-paginated `projects-page-*`
    // queries (staleTime 30s) that read `monitorStatusCounts`. Pausing/activating a
    // monitor here does not touch those caches, so the list page would keep showing
    // the stale active/paused counts until they expire. Invalidate them on refresh.
    queryClient.invalidateQueries({ queryKey: ['projects-page-ungrouped'] });
    queryClient.invalidateQueries({ queryKey: ['projects-page-group'] });
    // The ProjectsPage KPI columns (brand score / position / visibility) are computed for the
    // project's brand (firmName), so a brand switch here must refresh them too.
    queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KPIS] });
    await Promise.all([
      reloadProject(),
      reloadMonitorPrompts(),
      reloadTags(),
      reloadCommonKeywords(),
      reloadKeywordDailyStatsForProject(),
      reloadProjectDailyStats(),
      reloadEvents(),
      reloadAnomalyIncidents(),
      reloadAllBrandsStatsResponse(),
      reloadSovPerModel(),
      reloadVisibilityChart(),
      reloadSovTimeSeries(),
      reloadVisibilityPerMonitor(),
      reloadSourcesForProject(),
      refetchAccountSilently(),
      refetchProjectsSilently(),
    ]);
  }, [
    reloadProject,
    reloadMonitorPrompts,
    reloadTags,
    reloadCommonKeywords,
    reloadKeywordDailyStatsForProject,
    reloadProjectDailyStats,
    reloadEvents,
    reloadAnomalyIncidents,
    reloadAllBrandsStatsResponse,
    reloadSovPerModel,
    reloadVisibilityChart,
    reloadSovTimeSeries,
    reloadVisibilityPerMonitor,
    reloadSourcesForProject,
    refetchAccountSilently,
    refetchProjectsSilently,
    queryClient,
  ]);

  // Przeliczanie statystyk marki wybranej (po włączeniu jej nazwy) — tabele pokazują wtedy loadery.
  const { selectedBrandPending: pendingBrands, isSelectedBrandRecomputing: isRecomputing } =
    useKeywordStatsRecompute(projectIdNumber);
  const { confirmNewProjectKeywords } = useChooseKeywords({
    refreshProjects: onRefresh,
    keywordsPage: hasBrandGrouping,
  });

  const { editName } = useChangeName({ onRefresh });

  const { confirmRemove } = useRemoveProject({
    refreshProjects: () => {
      refetchData();
      navigate(PROJECTS);
    },
  });

  const downloadReport = async () => {
    const result = await reloadReport();
    if (!result || result.status !== 'success' || !result.data) {
      return;
    }

    const data = result.data as PdfReportResponse;
    let template = data.template as string;

    (data.chartAttachments || []).forEach((attachment: string) => {
      const [filename, base64] = attachment.split(': ');
      if (!filename || !base64) return;
      const cid = `cid:${filename.trim()}`;
      template = template.split(cid).join(`data:image/png;base64,${base64.trim()}`);
    });

    const parser = new DOMParser();
    const doc = parser.parseFromString(template, 'text/html');

    const images = doc.querySelectorAll('img');
    images.forEach((img) => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      img.style.margin = '0 auto';
    });

    template = doc.body.innerHTML;

    const styledTemplate = `
    <div style="
      width: 190mm; 
      max-width: 190mm; 
      margin: 0 auto; 
      padding: 10mm;
      box-sizing: border-box;
      font-family: Arial, sans-serif;
    ">
      ${template}
    </div>
  `;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = styledTemplate;

    await html2pdf()
      .from(wrapper)
      .set({
        filename: 'report.pdf',
        html2canvas: {
          scale: 2,
          useCORS: true,
          scrollY: 0,
          scrollX: 0,
          windowWidth: 1200,
          letterRendering: true,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
        },
        pagebreak: {
          mode: ['avoid-all', 'css'],
          avoid: '.report-section',
        },
      } as any)
      .save();
  };

  useProjectNavigate(projectIdNumber);
  useMonitorPeriodAndChatType(type, period, chat, projectIdNumber, accountResponse);
  const { exportStats, exportSources, exportBrandScore } = useExportData(projectIdNumber);

  const firmName = useMemo(() => {
    if (!project) {
      return '';
    }

    return project.firmName || '';
  }, [project]);

  // Canonical keys for matching against stats rows (tag key / normalized lowercase) — raw
  // firmName/defaultKeyword may keep display casing and would silently miss every row.
  const firmBrandKey = useMemo(() => resolveBrandKey(firmName, tags), [firmName, tags]);
  const defaultBrandKey = useMemo(
    () => resolveBrandKey(project?.defaultKeyword ?? '', tags),
    [project?.defaultKeyword, tags]
  );

  const firmNameOrg = useMemo(() => {
    if (!firmName || !project) {
      return '';
    }

    const tagId = commonKeywords ? findTagIdInKeyword(firmName, commonKeywords) : null;
    const brandName = getBrandDisplayName(firmName, project.firmNameOrg, commonKeywords, hasBrandGrouping);
    return tags ? getTagLabel(tags, tagId, brandName) : brandName;
  }, [project, tags, commonKeywords, firmName, hasBrandGrouping]);

  const nonEmptyPromptGroups = useMemo(() => {
    if (!promptGroups || !monitorPrompts) return [];
    const groupIdsWithPrompts = new Set(
      monitorPrompts.map((p) => p.promptGroupId).filter((id): id is number => id != null)
    );
    return promptGroups.filter((g) => groupIdsWithPrompts.has(g.id));
  }, [promptGroups, monitorPrompts]);

  const keywordsMap = useKeywordMapper(commonKeywords, monitorPrompts);
  const firmNameLabel = useMemo(() => {
    // For tag-linked brands (firmName starts with TAG_NAME prefix), keywordsMap
    // maps the raw tag key to itself (chatbeat_tag_name_X → chatbeat_tag_name_X),
    // which is not a useful display label. Skip the map lookup for tag prefixes
    // and go directly to getTagLabel, mirroring exactly what getPieChartWithOthers does.
    if (!firmName.startsWith(TAG_NAME)) {
      const mappedLabel = keywordsMap.get(firmName);
      if (mappedLabel) return mappedLabel;
    }

    // firmName is a tag-prefixed keyword (chatbeat_tag_name_<tagId>).
    // Resolve the same way getPieChartWithOthers does so that firmNameLabel
    // matches the display label stored in shareOfVoiceData slice names.
    const tagId = commonKeywords ? findTagIdInKeyword(firmName, commonKeywords) : null;
    if (tagId && tags) {
      return getTagLabel(tags, tagId, firmName);
    }

    return firmName;
  }, [keywordsMap, firmName, commonKeywords, tags]);

  const shareOfVoiceData = useShareOfVoiceData(
    allBrandsStatsResponse,
    keywordsMap,
    firmName,
    commonKeywords,
    tags
  );
  const promptsData = usePromptsData(
    isKeywordDailyStatsForProjectLoading,
    isEventsLoading,
    keywordsDailyStatsForProject,
    keywordsMap,
    filteredMonitorPrompts,
    selectedChatType,
    accountResponse,
    commonKeywords,
    tags,
    selectedChatTypes
  );
  // V2: merge per-question Visibility % (selected brand) onto each prompt row by monitor id (for the
  // Visibility column). ChatGPT-Ads / Shopping-Cards percentages arrive on promptsData from the backend.
  const promptsDataWithVisibility = useMemo(() => {
    if (!promptsData) return promptsData;
    const visibilityByMonitor = new Map(
      (visibilityPerMonitor ?? []).map((entry) => [entry.monitorId, entry])
    );
    if (visibilityByMonitor.size === 0) return promptsData;
    return promptsData.map((prompt) => {
      const entry = prompt.id != null ? visibilityByMonitor.get(prompt.id) : undefined;
      return entry
        ? { ...prompt, visibility: entry.visibility, visibilityCurrent: entry.visibilityCurrent }
        : prompt;
    });
  }, [promptsData, visibilityPerMonitor]);
  const projectStatsMatrix = useProjectStatsMatrix(
    isProjectDailyStatsLoading,
    isMonitorPromptsLoading,
    isEventsLoading,
    monitorPeriod ?? undefined,
    projectDailyStats
  );
  // V2: the Brand Position chart plots Visibility % (new metric) instead of Brand Score.
  // Built with the same prepareHeaderDate buckets so it aligns with the average-position series.
  const visibilityMatrix = useMemo(
    () => createVisibilityMatrix((monitorPeriod ?? 'WEEKLY') as PeriodType, visibilityChart),
    [monitorPeriod, visibilityChart]
  );
  // V2 "Brand Score & Visibility": both series are % on a single shared Y axis, so merge them
  // into one matrix [date, Brand Score, Visibility]. Visibility (the response-stats table) covers
  // every response day, so it's the more complete date set → use it as the base; fall back to the
  // brand-score matrix when visibility has no data yet (e.g. before backfill).
  const brandScoreVisibilityMatrix = useMemo<(string | number)[][]>(() => {
    const brandScoreRows = projectStatsMatrix?.matrix ?? [];
    const visibilityRows = visibilityMatrix ?? [];
    const header: (string | number)[] = ['date', t('brandScore'), t('visibility')];
    const nullValue = null as unknown as string | number;

    if (visibilityRows.length > 1) {
      const brandScoreByDate = new Map<string, string | number>(
        brandScoreRows.slice(1).map((row) => [String(row[0]), row[1]])
      );
      return [
        header,
        ...visibilityRows.slice(1).map((row) => {
          const rawBrandScore = brandScoreByDate.get(String(row[0])) ?? nullValue;
          // A visibility of 0 / non-number means "not measured for this bucket" — emit null so the
          // chart draws a gap instead of a point pinned to zero.
          const visibility =
            typeof row[1] === 'number' && row[1] > 0 ? row[1] : nullValue;
          return [row[0], blendBrandScore(rawBrandScore, row[1]) ?? nullValue, visibility];
        }),
      ];
    }

    // No visibility data yet — show brand score alone (no blend), visibility column null.
    return [header, ...brandScoreRows.slice(1).map((row) => [row[0], row[1], nullValue])];
  }, [projectStatsMatrix, visibilityMatrix, t]);

  // Brand Score tile value blended with Visibility (V2 only). Clones the firmName's all-brands entry
  // with the new brand score; the tile reads `.brandScore` from it. Trend is left as-is (raw).
  const brandScoreTileData = useMemo(() => {
    const entry = allBrandsStatsResponse.find((item) => item.keyword === firmBrandKey) || null;
    if (!entry) return null;
    const blended = blendBrandScore(entry.brandScore, projectVisibility?.visibility ?? null);
    return typeof blended === 'number' ? { ...entry, brandScore: blended } : entry;
  }, [allBrandsStatsResponse, firmBrandKey, projectVisibility]);

  // Brands ranking (BrandsTable): blend each brand's Brand Score with its own per-brand visibility.
  // Separate array so other consumers (pie / SoV) keep the raw entries — they use shareOfVoice, not
  // brandScore.
  const allBrandsStatsBlended = useMemo(() => {
    // Show ONLY brands that are selectable in the picker (ChangeKeywordBox) — replicate its filter:
    // commonKeywords gated by liveBrandKeys, plus the project brand + default keyword. Without this
    // the ranking lists brands present in the backend keyword list but NOT in the picker (orphans —
    // e.g. detected competitors / non-live keywords), which is confusing because you can't switch to
    // them. `commonKeywords` here is the SAME source the dropdown uses (useGetCommonKeywordsWithTags).
    const liveSet = new Set(liveBrandKeys ?? []);
    const applyLive = liveSet.size > 0;
    const allowed = new Set<string>();
    for (const kw of commonKeywords ?? []) {
      const resolvedKey = kw.tagId ? `${TAG_NAME}${kw.tagId}` : kw.normalizedKeyword;
      if (
        !applyLive ||
        liveSet.has(resolvedKey) ||
        resolvedKey === firmBrandKey ||
        resolvedKey === defaultBrandKey
      ) {
        allowed.add(resolvedKey);
      }
    }
    if (firmBrandKey) allowed.add(firmBrandKey);

    // Until the keyword list has loaded, `allowed` only holds `firmName`, which would collapse the
    // ranking to the monitored brand alone (while the pie still shows everyone). Skip the orphan filter
    // in that window so the ranking matches the pie; it tightens up once commonKeywords arrives.
    const hasKeywordList = (commonKeywords?.length ?? 0) > 0;

    return (
      hasKeywordList
        ? allBrandsStatsResponse.filter((entry) => allowed.has(entry.keyword))
        : allBrandsStatsResponse
    ).map((entry) => {
      const blended = blendBrandScore(entry.brandScore, entry.visibility);
      return typeof blended === 'number' ? { ...entry, brandScore: blended } : entry;
    });
  }, [allBrandsStatsResponse, commonKeywords, liveBrandKeys, firmBrandKey, defaultBrandKey]);
  // V2 "Brand Score & Visibility": optional, opt-in average-position series for the chart's
  // secondary (inverted) axis. projectStatsMatrix.secondMatrix is [date, averagePosition] keyed by
  // the same prepareHeaderDate buckets as brandScore — re-key it onto the merged matrix's date set
  // so both axes share identical x categories (the merged matrix uses the more complete visibility dates).
  const averagePositionMatrix = useMemo<(string | number)[][]>(() => {
    const header: (string | number)[] = ['date', t('averagePosition')];
    const positionRows = projectStatsMatrix?.secondMatrix ?? [];
    if (positionRows.length <= 1) return [header];
    const positionByDate = new Map<string, string | number>(
      positionRows.slice(1).map((row) => [String(row[0]), row[1]])
    );
    const nullValue = null as unknown as string | number;
    return [
      header,
      ...brandScoreVisibilityMatrix
        .slice(1)
        .map((row) => [row[0], positionByDate.get(String(row[0])) ?? nullValue]),
    ];
  }, [projectStatsMatrix, brandScoreVisibilityMatrix, t]);
  // chatType → canonicalKeyword → per-prompt SoV%. Keys are canonical/tag-resolved (same space as
  // allBrandsStatsResponse / firmName), so they line up with the matrix's selection + keywordsMapper.
  const perModelSov = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    sovPerModel.forEach(({ chatType, keyword, shareOfVoice }) => {
      if (!map.has(chatType)) map.set(chatType, new Map());
      map.get(chatType)!.set(keyword, shareOfVoice);
    });
    return map;
  }, [sovPerModel]);

  const allModelsStatsMatrix = useAllModelsStatsMatrix(
    isProjectDailyStatsLoading || isSovPerModelLoading,
    isMonitorPromptsLoading,
    isEventsLoading,
    firmName,
    accountResponse,
    shareOfVoiceData,
    keywordsMap,
    perModelSov,
    hasExplicitModelFilter,
    selectedChatTypes
  );
  // When a superadmin views a client's project, resolve models from the OWNER's features (project.
  // ownerFeatures) so the model set matches the client's view; otherwise the logged-in account's.
  const effectiveFeatures = project?.ownerFeatures ?? accountResponse?.features ?? [];

  const modelsWithoutData = useModelsWithoutData(
    accountResponse,
    keywordsDailyStatsForProject,
    isKeywordDailyStatsForProjectLoading,
    range,
    project?.ownerFeatures
  );

  // Enabled models + readiness — drives the Brand Score chart's collecting-state progress
  // (a model is "ready" once it has data, i.e. not in modelsWithoutData).
  const collectingModels = useMemo(
    () =>
      getEnabledModelKeys(effectiveFeatures).map((key) => ({
        key,
        ready: !modelsWithoutData.includes(key),
      })),
    [effectiveFeatures, modelsWithoutData]
  );

  const brandColors = useChartColors(shareOfVoiceData, firmNameLabel, keywordsMap);
  const currentBrandGeneralStats = useCurrentBrandGeneralStats(firmName, allBrandsStatsResponse, tags);

  // Fresh projects (≤3 days old) default the Brand Score & Visibility panel to the per-brand bar view
  // (a sparse line chart isn't useful yet); older projects default to the line chart. User can toggle.
  const isNewProject =
    !!project?.created &&
    Date.now() - new Date(project.created).getTime() <= 3 * 24 * 60 * 60 * 1000;
  const visibilityBarDaysCount = Math.max(1, brandScoreVisibilityMatrix.length - 1);

  // resolveLabel mirrors BrandsTable.resolveKeyword: tag id → tag label → keywordsMap lookup.
  const resolveLabel = useCallback(
    (keyword: string): string => {
      const tagId = commonKeywords ? findTagIdInKeyword(keyword, commonKeywords) : null;
      const tagLabel = tags ? getTagLabel(tags, tagId, keyword) : keyword;
      return keywordsMap.get(tagLabel) || tagLabel;
    },
    [commonKeywords, tags, keywordsMap]
  );

  // Bar view of the Brand Score & Visibility panel: per-brand VISIBILITY % (label-resolved), colored
  // like the other brand charts (monitored brand gets its highlight color).
  const brandVisibilityBars = useMemo(
    () =>
      allBrandsStatsBlended
        .filter((b) => typeof b.visibility === 'number')
        .map((b) => ({ name: resolveLabel(b.keyword), value: b.visibility as number })),
    [allBrandsStatsBlended, resolveLabel]
  );
  const brandVisibilityColors = useChartColors(brandVisibilityBars, firmNameLabel, keywordsMap);

  // Show EXACTLY the brands the "SoV by AI Chatbots" bar chart picked, so the two charts always
  // agree. `selectedKeywords` are derived from `perModelSov`, whose keys are tag-resolved
  // (COALESCE(chatbeat_tag_name_<id>, keyword)) — the SAME space as the sov-time-series points — so
  // they line up with `sovTimeSeries`. (Deriving them separately from `allBrandsStatsResponse` via
  // `getTopSovBrands` could rank brands differently and show a different set than the bar.)
  const sovTopBrands = allModelsStatsMatrix.selectedKeywords;

  const sovOverTimeMatrix = useMemo(
    () => createSovOverTimeMatrix(period as PeriodType, sovTimeSeries, sovTopBrands, resolveLabel),
    [period, sovTimeSeries, sovTopBrands, resolveLabel]
  );
  // V2 "Brand Score & Visibility" chart: both series are %. Legend state map is keyed by
  // these labels, so they must match the chart series names (Brand Score = first, Visibility = second).
  const monitorHeaders = [t('brandScore'), t('visibility')];
  const visibilityTexts = useVisibilityTexts(
    firmName,
    firmNameLabel,
    currentBrandGeneralStats,
    promptsData,
    selectedChatType,
    accountResponse,
    allModelsStatsMatrix.matrix,
    tags,
    commonKeywords
  );

  const handleExport = async () => {
    if (isDownloading || !canExport) {
      return;
    }

    try {
      setIsDownloading(true);
      await exportStats();
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDailyStatsExport = async () => {
    if (isDownloadingBrandScore || !canExport) {
      return;
    }

    try {
      setIsDownloadingBrandScore(true);
      await exportBrandScore(monitorPeriod);
    } finally {
      setIsDownloadingBrandScore(false);
    }
  };

  const handleDownload = async () => {
    if (isDownloadingReport) {
      return;
    }

    try {
      setIsDownloadingReport(true);
      await downloadReport();
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const isProjectDataReady = useMemo(() => {
    if (isProjectDailyStatsLoading || isMonitorPromptsLoading || isAllBrandsStatsResponseLoading) {
      return false;
    }

    return (
      !!allBrandsStatsResponse &&
      allBrandsStatsResponse.length > 0 &&
      !!monitorPrompts &&
      monitorPrompts.length > 0 &&
      !hasAnyPendingQuestions
    );
  }, [
    allBrandsStatsResponse,
    monitorPrompts,
    hasAnyPendingQuestions,
    isProjectDailyStatsLoading,
    isMonitorPromptsLoading,
    isAllBrandsStatsResponseLoading,
  ]);

  useFirstViewTracking({
    projectId: projectIdNumber,
    accountResponse,
    isDataReady: isProjectDataReady,
  });

  // Date-bucket rows exist even when every value is null (nothing collected for the period), so a
  // non-empty matrix isn't enough. Require at least one real Brand Score / Visibility number —
  // otherwise the chart and tiles render empty points instead of the empty state.
  const hasProjectStatsData = brandScoreVisibilityMatrix
    .slice(1)
    .some((row) => Number.isFinite(row[1] as number) || Number.isFinite(row[2] as number));
  const isFiltersReady = !filtersInitializing;

  if (!accountResponse || !projectResponse || !project) {
    return <></>;
  }

  if (!isMonitorPromptsLoading && monitorPrompts && monitorPrompts.length === 0) {
    return (
      <ProjectPreviewEmptyState
        firmNameOrg={firmNameOrg}
        isSuperAdmin={accountResponse.isSuperAdmin}
        currentProject={project || null}
        projectId={projectId}
        demoVersion={project?.demoVersion || null}
        accountStatus={accountResponse.accountStatus}
        canManage={!!canManage}
      />
    );
  }

  // Only block until the project shell can render. Prompts/tags/stats loading is handled in-place
  // by each component's own isLoading skeleton — so the real layout appears immediately and fills
  // in without a layout jump.
  if (!projectResponse || !accountResponse) {
    return <ProjectPreviewSkeleton />;
  }

  if (!projectIdNumber) {
    navigate(PROJECTS);
    return null;
  }

  const isAllSelected = selectedChatType === 'ALL';

  const isShareOfVoiceLoading =
    !isFiltersReady ||
    isProjectDailyStatsLoading ||
    isMonitorPromptsLoading ||
    isAllBrandsStatsResponseLoading ||
    isSovPerModelLoading ||
    isEventsLoading;

  // Refresh flags go ONLY into components' `isLoading` props — never into the section gates above/below,
  // which would unmount whole sections on every date change.
  const isShareOfVoiceRefreshing =
    isProjectDailyStatsRefreshing || isAllBrandsStatsResponseRefreshing || isSovPerModelRefreshing;

  // Nagłówek strony: na desktopie w `Title`; na telefonie tytuł i menu projektu w górnym pasku
  // (`MobilePageHeader`), a filtry na górze strony, pod paskiem.
  const titleIconClass = `ico--md  ${
    project.localization ? `fi-${project.localization.toLowerCase()}` : 'ico--globe'
  } `;
  const headerFilters = (
    <>
      {!project.demoVersion && (
        <ChangeKeywordBox
          project={project}
          changeKeyword={onRefresh}
          liveKeys={liveBrandKeys}
        />
      )}
      <FilterPanel baseUrl={location.pathname}>
        <PromptGroupDropdown projectId={projectIdNumber} />
      </FilterPanel>
    </>
  );
  const headerMore = (
    <>
      {/* Wszystko w tym menu wychodzi poza podgląd: edycja projektu, eksporty, raport
          PDF i usunięcie projektu. Dla widza bez prawa zapisu znika w całości. */}
      {projectsAccess.write && (
      <MoreBtn rowId={0}>
        <div className="buttons-list">
          {!project?.demoVersion &&
            !['BLOCKED', 'DISABLED'].includes(accountResponse?.accountStatus) && (
              <>
                <Button
                  id={`btn-edit-project-${projectIdNumber}`}
                  className="action-btn material-symbols-outlined"
                  onClick={() => editName(projectIdNumber)}
                >
                  <span className="ico--edit ico--xs" />
                </Button>
                <CustomTooltip
                  tooltipText="editProject"
                  targetRef={`btn-edit-project-${projectIdNumber}`}
                />
                <Button
                  id={`btn-download-stats-${projectIdNumber}`}
                  className={`action-btn material-symbols-outlined ${
                    !canExport ? 'btn--disabled-tooltip' : ''
                  }`}
                  onClick={handleExport}
                >
                  {isDownloading ? (
                    <span className="ico--loader ico--xs" />
                  ) : (
                    <span
                      className={`ico--download ${
                        !canExport ? 'ico--disabled' : ''
                      } ico--xs`}
                    />
                  )}
                </Button>
                <CustomTooltip
                  tooltipText={
                    !canExport ? 'upgradePlanTrialExportMsg' : 'downloadPromptStats'
                  }
                  targetRef={`btn-download-stats-${projectIdNumber}`}
                />
                <Button
                  id={`btn-download-brand-score-${projectIdNumber}`}
                  className={`action-btn material-symbols-outlined ${
                    !canExport ? 'btn--disabled-tooltip' : ''
                  }`}
                  onClick={handleDailyStatsExport}
                >
                  {isDownloadingBrandScore ? (
                    <span className="ico--loader ico--xs" />
                  ) : (
                    <span className={`ico--download ico--xs`} />
                  )}
                </Button>
                <CustomTooltip
                  tooltipText={
                    !canExport ? 'upgradePlanTrialExportMsg' : 'downloadBrandScore'
                  }
                  targetRef={`btn-download-brand-score-${projectIdNumber}`}
                />
              </>
            )}
          {accountResponse?.addons.includes('REPORTS') && (
            <>
              <Button
                id={`btn-pdf-report-${projectIdNumber}`}
                className="action-btn material-symbols-outlined"
                onClick={handleDownload}
              >
                {isDownloadingReport ? (
                  <span className="ico--loader ico--xs" />
                ) : (
                  <span className="ico--report ico--xs" />
                )}
              </Button>
              <CustomTooltip
                tooltipText="downloadPdfReport"
                targetRef={`btn-pdf-report-${projectIdNumber}`}
              />
            </>
          )}
          {accountResponse?.isSuperAdmin && !accountResponse?.isLimitedSuperAdmin && (
            <>
              <Button
                id={`btn-regen-suggested-${projectIdNumber}`}
                className="action-btn material-symbols-outlined"
                onClick={() => setRegenerateDialogOpen(true)}
                disabled={isRegenerating}
              >
                <span className="ico--regenerate ico--xs" />
              </Button>
              <CustomTooltip
                tooltipText="regenerateSuggestedPrompts"
                targetRef={`btn-regen-suggested-${projectIdNumber}`}
              />
            </>
          )}
          <Button
            id={`btn-remove-project-${projectIdNumber}`}
            className="action-btn material-symbols-outlined"
            onClick={() => confirmRemove(projectIdNumber, project?.projectLabel)}
          >
            <span className="ico--trash ico--xs" />
          </Button>
          <CustomTooltip
            tooltipText="removeProject"
            targetRef={`btn-remove-project-${projectIdNumber}`}
          />
        </div>
      </MoreBtn>
      )}
    </>
  );

  return (
    <>
      <div className="preview-project-page page-content">
        <div className="page-box">
          <MobilePageHeader
            title={
              <>
                <span className={titleIconClass} />
                <span className="capitalize">{firmNameOrg}</span>
              </>
            }
            actions={headerMore}
          />
          {isMobile && <div className="mobile-page-filters">{headerFilters}</div>}
          {!isMobile && (
            <div className="page-box__filters" data-observe-sticky>
              <Title
                isBaseCategory={false}
                title={firmNameOrg}
                titleClassName="capitalize"
                titleStyle={titleIconClass}
                children={
                  <div className="filters">
                    {headerFilters}
                    {headerMore}
                  </div>
                }
              />
            </div>
          )}

          <>
            {hasAnyPendingQuestions && (
              <div className="data-collecting-notice">
                <div className="status status--pending">
                  <div className="status__ring" />
                  <div className="status__dot" />
                </div>
                <span className="data-collecting-notice__text">{t('dataCollectingNotice')}</span>
              </div>
            )}

            <div className="page-box__stats page-box__stats--without-bottom-padding">
              <ProjectStatsTiles
                projectGeneralData={brandScoreTileData}
                visibilityPercent={projectVisibility?.visibility ?? null}
                useAveragePosition
                collecting={!hasProjectStatsData && isInitialCollecting}
                recomputing={isRecomputing}
                isLoading={
                  !isFiltersReady ||
                  isProjectDailyStatsLoading ||
                  isProjectDailyStatsRefreshing ||
                  isAllBrandsStatsResponseRefreshing ||
                  isMonitorPromptsLoading ||
                  (!hasProjectStatsData && isInitialCollecting)
                }
              />

              <div className="stats">
                <BrandPositionChart
                  // V2 "Brand Score & Visibility": both series (Brand Score %, Visibility %) live in
                  // one matrix on a single shared % axis. brandScoreData is unused in percentageMode.
                  averagePositionData={brandScoreVisibilityMatrix}
                  averagePositionSecondaryData={averagePositionMatrix}
                  brandScoreData={[]}
                  monitorHeaders={monitorHeaders}
                  eventsData={events}
                  hasAnyPendingQuestions={isInitialCollecting}
                  hasProjectStatsData={hasProjectStatsData}
                  recomputingBrands={pendingBrands}
                  collectingModels={collectingModels}
                  isLoading={
                    !isFiltersReady ||
                    isProjectDailyStatsLoading ||
                    isProjectDailyStatsRefreshing ||
                    isMonitorPromptsLoading ||
                    isEventsLoading ||
                    isVisibilityChartLoading ||
                    isVisibilityChartRefreshing ||
                    (canShowAnomalyMarkers && isAnomalyIncidentsLoading)
                  }
                  averagePositionAnomalies={averagePositionAnomalies}
                  brandScoreAnomalies={brandScoreAnomalies}
                  visibilityAnomalies={visibilityAnomalies}
                  metricSemantics={metricSemantics}
                  period={monitorPeriod}
                  percentageMode
                  brandVisibilityData={brandVisibilityBars}
                  brandColors={brandVisibilityColors}
                  daysCount={visibilityBarDaysCount}
                  selectedBrand={firmNameLabel}
                  defaultChartView={isNewProject ? 'bar' : 'line'}
                />
                <ShareOfVoiceChart
                  key={`share-of-voice-chart-${projectId}`}
                  shareOfVoiceData={shareOfVoiceData}
                  hasAnyPendingQuestions={isInitialCollecting}
                  firmName={firmNameLabel}
                  hasProjectStatsData={hasProjectStatsData}
                  onSettingsClick={() =>
                    confirmNewProjectKeywords(
                      projectIdNumber,
                      () => project.firmName,
                      commonKeywords || [],
                      tags,
                      liveBrandKeys,
                      project.defaultKeyword
                    )
                  }
                  isLoading={isShareOfVoiceLoading || isShareOfVoiceRefreshing}
                  recomputingBrands={pendingBrands}
                />
              </div>

              {/* Show whenever ANY brand has SoV data (not just the selected one) — the matrix
                  force-adds the monitored brand as a 0 column when other brands exist, so a 0-SoV
                  selected brand must NOT hide the section. Gating on `!isShareOfVoiceLoading`
                  (includes `isSovPerModelLoading`) + `allBrandsStatsResponse.length` keeps it hidden
                  while loading/pending without depending on the selected brand's own data. */}
              {isAllSelected &&
                !isSingleModelSelected &&
                !isShareOfVoiceLoading &&
                !isCommonKeywordsLoading &&
                allBrandsStatsResponse &&
                allBrandsStatsResponse.length > 0 &&
                allModelsStatsMatrix.matrix &&
                allModelsStatsMatrix.matrix.length > 0 &&
                brandColors &&
                brandColors.length > 0 && (
                  <div className="stats stats--scroll stats--full-width">
                    <ShareOfVoiceBarChart
                      allModelsStatsMatrix={allModelsStatsMatrix.matrix}
                      brandColors={brandColors}
                      firmName={firmNameLabel}
                      keywordsMapper={keywordsMap}
                      commonKeywords={commonKeywords}
                      tags={tags}
                      modelDataRanges={modelDataRanges}
                    />
                  </div>
                )}

              {/* Hidden until the project has real brand data — so while every monitor is still
                  pending (empty aggregates) this whole section disappears, exactly like V1's
                  Insights gate. A partially-pending project with some data still shows it. */}
              {!isShareOfVoiceLoading &&
                !isCommonKeywordsLoading &&
                allBrandsStatsResponse &&
                allBrandsStatsResponse.length > 0 && (
                  <div className="stats">
                    <ShareOfVoiceOverTimeChart
                      matrix={sovOverTimeMatrix}
                      brandColors={brandColors}
                      period={monitorPeriod}
                      isLoading={
                        isShareOfVoiceLoading ||
                        isShareOfVoiceRefreshing ||
                        isSovTimeSeriesLoading ||
                        isSovTimeSeriesRefreshing ||
                        isRecomputing
                      }
                      hasAnyPendingQuestions={isInitialCollecting}
                    />
                    <InsightsBox
                      hasAnyPendingQuestions={isInitialCollecting}
                      visibilityTexts={visibilityTexts}
                    />
                  </div>
                )}

              {!isShareOfVoiceLoading &&
                !isCommonKeywordsLoading &&
                allBrandsStatsResponse &&
                allBrandsStatsResponse.length > 0 &&
                accountResponse.addons.includes('TABLE') && (
                  <div className="stats stats--full-width">
                    <BrandsTable
                      recomputingBrands={pendingBrands}
                      allBrandsStatsResponse={allBrandsStatsBlended}
                      isLoading={isAllBrandsStatsResponseRefreshing}
                      keywordsMapper={keywordsMap}
                      commonKeywords={commonKeywords}
                      tags={tags}
                      showVisibility
                      showAveragePosition
                      mainBrandKey={defaultBrandKey}
                      selectedBrandKey={firmBrandKey}
                    />
                  </div>
                )}
            </div>

            <div id="key-prompts">
              <PromptsTable
                tableTitle={t('keyPromptsTitle')}
                tableTitleSubject={t('keyPromptsFor', { name: firmNameOrg })}
                data={promptsDataWithVisibility}
                refreshAfterDelete={onRefresh}
                range={range}
                projectId={projectIdNumber}
                chatType={selectedChatType ?? 'GPT'}
                periodType={monitorPeriod ?? null}
                isLoading={
                  !isFiltersReady ||
                  isKeywordDailyStatsForProjectLoading ||
                  isKeywordDailyStatsForProjectRefreshing ||
                  isVisibilityPerMonitorRefreshing ||
                  isRecomputing
                }
                modelsWithoutData={modelsWithoutData}
                modelDataRanges={modelDataRanges}
                showVisibility
                showFeatures
                alwaysShowAccountModels
                selectedChatTypes={selectedChatTypes}
                additionalClass=""
                showKeyword={false}
                groups={
                  hasPromptGroupsAddon
                    ? promptGroupId === ALL_PROMPT_GROUPS_ID
                      ? (nonEmptyPromptGroups as PromptGroupItem[])
                      : (nonEmptyPromptGroups as PromptGroupItem[]).filter(
                          (g) => g.id === promptGroupId
                        )
                    : undefined
                }
                hasPromptGroupsAddon={hasPromptGroupsAddon}
                groupDisplayMode={hasPromptGroupsAddon ? 'accordion' : undefined}
                scrollHeight="70vh"
              />
            </div>

            {/* Source-based sections (Top Domains, AI Citations, Key Sources) hide until the project
                has collected sources — same gate as V1's LinkTrackingTable, so a fully-pending project
                shows none of them. */}
            {!isProjectDailyStatsLoading &&
              !isMonitorPromptsLoading &&
              sourcesForProject.linksData &&
              sourcesForProject.linksData.length > 0 && (
                <>
                  <div className="page-box__stats page-box__stats--without-bottom-padding">
                    <div className="stats">
                      {/* Top Domains is a cross-model breakdown — hide it when a single model is
                          selected, where the breakdown carries no extra information. */}
                      {!isSingleModelSelected && (
                        <TopDomainsTable
                          projectId={projectIdNumber}
                          chatType={selectedChatType}
                          startDate={startDate}
                          endDate={endDate}
                          monitorPromptIds={
                            filteredMonitorPrompts
                              ? filteredMonitorPrompts
                                  .map((p) => p.id)
                                  .filter((id): id is number => id != null)
                              : undefined
                          }
                          filterMonitorTypes={filterMonitorTypes}
                        />
                      )}
                      <AiCitations
                        sources={sourcesForProject.linksData || []}
                        isAllSelected={isAllSelected}
                      />
                    </div>
                  </div>

                  <KeySources
                    projectId={projectIdNumber}
                    chatType={selectedChatType}
                    startDate={startDate}
                    endDate={endDate}
                    domains={sourcesForProject.domains || []}
                    monitorPromptIds={
                      filteredMonitorPrompts
                        ? filteredMonitorPrompts
                            .map((p) => p.id)
                            .filter((id): id is number => id != null)
                        : undefined
                    }
                    filterMonitorTypes={filterMonitorTypes}
                    isDemo={!!project.demoVersion}
                    exportSources={exportSources}
                    firmName={firmName.startsWith(TAG_NAME) ? firmNameLabel : firmName}
                    tags={tags ?? []}
                    hasAdvancedFeatures={!!accountResponse?.addons?.includes('SOURCES_STATS_ADVANCED')}
                  />
                </>
              )}
          </>
        </div>
      </div>
      {toastElement}
      <Dialog
        header={t('regenerateSuggestedPrompts')}
        visible={regenerateDialogOpen}
        onHide={() => !isRegenerating && setRegenerateDialogOpen(false)}
        className="dialog-box regenerate-prompts-dialog"
        style={{ width: '460px' }}
        dismissableMask={!isRegenerating}
      >
        <div className="regenerate-prompts-dialog__content">
          <p className="regenerate-prompts-dialog__desc">
            <Trans i18nKey="regenerateSuggestedPromptsDesc" components={{ bold: <strong /> }} />
          </p>
          <Dropdown
            value={regenerateLanguage}
            onChange={(e) => setRegenerateLanguage(e.value)}
            options={regenerateLanguageOptions}
            optionLabel="name"
            placeholder={t('forceLanguageOptional')}
            className="dropdown"
            showClear
          />
          <div className="regenerate-prompts-dialog__footer">
            <Button
              className="btn btn--sm btn--secondary"
              label={t('cancel')}
              onClick={() => setRegenerateDialogOpen(false)}
              disabled={isRegenerating}
            />
            <Button
              className="btn btn--sm"
              label={isRegenerating ? t('regenerating') : t('regenerate')}
              disabled={isRegenerating}
              onClick={async () => {
                if (isRegenerating) return;
                setIsRegenerating(true);
                try {
                  const result = await regenerateSuggestedQuestions(
                    projectIdNumber,
                    regenerateLanguage?.type ?? null
                  );
                  showToast(t('regenerateSingleSuccess', { deleted: result.deletedCount }), 'success');
                  setRegenerateDialogOpen(false);
                  setRegenerateLanguage(null);
                } catch (err) {
                  console.error('Regenerate failed', err);
                  showToast(t('regenerateError'), 'error');
                } finally {
                  setIsRegenerating(false);
                }
              }}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ProjectsPreviewPageV2;
